import { useMemo, useState } from "react";
import { Button } from "@/design-system/components/ui";

// External party portals — downloadable statements (PR-E). A shared month
// picker + download button used by all four portals. Offers the last
// `monthsBack` months; the chosen month is rendered server-side into a PDF on
// demand (no persisted artifact). An empty month renders a graceful
// "no activity" statement, so a fixed recent-months list is fine.

function lastMonths(count: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  let year = now.getUTCFullYear();
  let month = now.getUTCMonth(); // 0-based
  for (let i = 0; i < count; i += 1) {
    out.push({
      value: `${year}-${String(month + 1).padStart(2, "0")}`,
      label: new Date(Date.UTC(year, month, 1)).toLocaleString("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }),
    });
    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }
  return out;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function StatementDownloadBar({
  onDownload,
  filename,
  monthsBack = 12,
  label = "Download statement",
  monthAriaLabel = "Statement month",
}: {
  /** Fetches the statement PDF for the chosen period as a Blob. */
  onDownload: (period: string) => Promise<Blob>;
  /** Download filename for the chosen period. */
  filename: (period: string) => string;
  monthsBack?: number;
  /** What the download is called (the bar's visible label). Defaults to the
   *  seller/broker/vendor "statement" wording; the buyer surface renames the
   *  ephemeral download "account activity". */
  label?: string;
  /** Accessible name for the month picker; pair it with a custom label. */
  monthAriaLabel?: string;
}) {
  const months = useMemo(() => lastMonths(monthsBack), [monthsBack]);
  const [period, setPeriod] = useState(months[0]?.value ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await onDownload(period);
      triggerDownload(blob, filename(period));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't generate the download.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-space-3 rounded-lg border border-border-light bg-white-warm p-space-3"
      data-testid="statement-download-bar"
    >
      <span className="text-small font-medium text-onyx">{label}</span>
      <select
        aria-label={monthAriaLabel}
        className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx"
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
      >
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleDownload}
        disabled={busy}
        data-testid="statement-download-button"
      >
        {busy ? "Preparing…" : "Download PDF"}
      </Button>
      {error && (
        <span className="text-caption text-status-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
